import { setup } from '@incodetech/web';

import { startIncodeSession, setIncodeConsent, getIncodeConfig } from '@evidentid/ido-lib/idoWebClient';

/**
 * Starts an Incode session for the given configuration ID.
 * Calls POST /incode/start-session via idoWebClient.
 */
export async function startSession(configId: string): Promise<{ token: string, interviewId: string }> {
    return startIncodeSession(configId);
}

interface ConsentPayload {
    title: string;
    content: string;
    status: boolean;
}

/**
 * Relays the user's biometric consent to Incode via the BE proxy endpoint.
 * Must be called after startSession so the session token is available.
 */
export async function setConsent(sessionToken: string, consent: ConsentPayload): Promise<void> {
    return setIncodeConsent(sessionToken, consent);
}

// setup() is not idempotent — calling it multiple times (e.g. after a retry) breaks the SDK.
// Guard with a module-level flag so we only call it once per page load.
let sdkInitialized = false;

/**
 * Initialises the Incode SDK with the provided session token.
 * Must be called after startSession; a single setup() call is all that is needed.
 * NOTE: apiUrl must end with /0 (e.g. https://demo-api.incodesmile.com/0).
 */
export async function initSdk(token: string): Promise<void> {
    // On retry, setup() is intentionally skipped. The new session token is passed
    // directly to <incode-flow> via its config property, which the SDK reads per-session.
    // The apiURL / WASM pipeline initialised by setup() is session-agnostic.
    if (!sdkInitialized) {
        const { apiUrl } = await getIncodeConfig();
        await setup({ apiURL: `${apiUrl}/0`, token });
        sdkInitialized = true;
    }
}

interface PollOptions {
    check: () => Promise<boolean>;
    maxAttempts: number;
    delayMs: number;
    isCancelled?: () => boolean;
}

/**
 * Polls check() on a fixed interval until it returns true or maxAttempts is exhausted.
 * Returns 'complete' when check() resolves true, 'timeout' when attempts run out,
 * or 'cancelled' when isCancelled() returns true.
 */
export async function pollForCompletion(opts: PollOptions): Promise<'complete' | 'timeout' | 'cancelled'> {
    const { check, maxAttempts, delayMs, isCancelled } = opts;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (isCancelled?.()) {
            return 'cancelled';
        }
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        if (isCancelled?.()) {
            return 'cancelled';
        }
        const done = await check();
        if (done) {
            return 'complete';
        }
    }

    return 'timeout';
}
